import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, Box, Chip, SxProps, Theme } from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import Chips from 'src/components/common/chips/Chips';
import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import { SiteRole } from 'src/features/auth';
import { getStudies, STUDY_LOADING_KEY } from 'src/features/study/store';
import {
	IStudyAssignmentTableProps,
	IStudyWithAssignment,
	StudyAssignmentStatus,
} from 'src/features/study_assignment/domain/types';
import { AppDispatch, RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';
import { snakeToTitleCase } from 'src/utils/formatter';

const getStudyAssignmentStatusChipSx = (
	status: StudyAssignmentStatus | string | undefined,
): SxProps<Theme> | undefined => {
	switch (status) {
		case 'completed':
			return {
				backgroundColor: '#E8F5E9',
				color: '#2E7D32',
				'& .MuiChip-label': { color: '#2E7D32' },
			};
		case 'in_progress':
			return {
				backgroundColor: '#FFF3E0',
				color: '#ED6C02',
				'& .MuiChip-label': { color: '#ED6C02' },
			};
		case 'ungradable':
			return {
				backgroundColor: '#FFEBEE',
				color: '#C62828',
				'& .MuiChip-label': { color: '#C62828' },
			};
		default:
			return undefined;
	}
};

export const StudyAssignmentTable: React.FC<IStudyAssignmentTableProps> = ({
	siteSlug,
	refreshTable,
	onStudyAssignmentChange,
	onUnassignClick,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const userProfile = useSelector(
		(state: RootState) => state.user_profile.profile,
	);

	const [queryState, setQueryState] = useState<ITableQueryState>({ page: 0 });
	const [studiesWithAssignment, setStudiesWithAssignment] = useState<
		IStudyWithAssignment[]
	>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [selectedStudyIds, setSelectedStudyIds] = useState<number[]>([]);
	const selectedStudies = useRef<Record<number, IStudyWithAssignment>>({});

	const isAdminUser = useMemo(
		() =>
			(['super_admin', 'admin'] as SiteRole[]).some((role) =>
				siteDetails?.roles.includes(role),
			),
		[siteDetails?.roles],
	);

	const isAnnotatorUser = useMemo(
		() =>
			!isAdminUser && (siteDetails?.roles.includes('annotator') ?? false),
		[siteDetails?.roles, isAdminUser],
	);

	const getCurrentUserAssignmentStatus = (
		studyWithAssignment: IStudyWithAssignment,
	): StudyAssignmentStatus | undefined => {
		const assignment = studyWithAssignment.assigned_user?.find(
			(user) => user.user_id === userProfile?.id,
		);
		return assignment?.status;
	};

	const isRowSelectable = (_row: IStudyWithAssignment) => isAdminUser;

	const handleSelectionChange = (ids: number[]) => {
		setSelectedStudyIds(ids);

		const nextSelectedRecord: Record<number, IStudyWithAssignment> = {};

		ids.forEach((id) => {
			if (selectedStudies.current[id]) {
				nextSelectedRecord[id] = selectedStudies.current[id];
			} else {
				const study = studiesWithAssignment.find((s) => s.id === id);
				if (study) {
					nextSelectedRecord[id] = study;
				}
			}
		});

		selectedStudies.current = nextSelectedRecord;
	};

	const handleAssignClick = () => {
		const studiesArray = Object.values(selectedStudies.current);

		if (studiesArray.length > 0) {
			onStudyAssignmentChange(studiesArray);
		}
	};

	const columns: IColumn<IStudyWithAssignment>[] = useMemo(() => {
		const assignmentColumn: IColumn<IStudyWithAssignment> = isAnnotatorUser
			? {
					key: 'assignment_status',
					label: 'Assignment Status',
					render: (studyWithAssignment) => {
						const status =
							getCurrentUserAssignmentStatus(studyWithAssignment);
						if (!status) return <i>N/A</i>;

						return (
							<Chip
								label={snakeToTitleCase(status)}
								size='small'
								sx={getStudyAssignmentStatusChipSx(status)}
							/>
						);
					},
				}
			: {
					key: 'assigned_user',
					label: 'Assigned Users',
					render: (studiesWithAssignment) => {
						if (!studiesWithAssignment.assigned_user)
							return <i>N/A</i>;
						const chips = studiesWithAssignment.assigned_user.map(
							(user) => ({
								name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
								sx: getStudyAssignmentStatusChipSx(user.status),
								tooltipTitle: `Assignment status: ${snakeToTitleCase(user.status)}`,
							}),
						);
						return <Chips tags={chips} show={1} />;
					},
				};

		return [
			{
				key: 'patient_study_id',
				label: 'Study ID',
				sortable: false,
				searchable: true,
				render: (studyWithAssignment) =>
					studyWithAssignment.patient_study_id,
			},
			{
				key: 'site_patient_id',
				label: 'Patient ID',
				render: (studyWithAssignment) =>
					studyWithAssignment.site_patient_id,
			},
			assignmentColumn,
			{
				key: 'created_at',
				label: 'Created At',
				sortable: true,
				render: (studyWithAssignment) =>
					formatTimestamp(studyWithAssignment.created_at),
			},
			{
				key: 'actions',
				label: 'Actions',
				render: (studyWithAssignment: IStudyWithAssignment) => {
					return (
						<Box
							display='flex'
							gap={1}
							sx={{
								width: '160px',
							}}
						>
							<Button
								variant='outlined'
								size='small'
								onClick={() =>
									navigate(
										`/site/${siteSlug}/patient/${studyWithAssignment.patient_id}/study/${studyWithAssignment.id}/study-annotation`,
									)
								}
								sx={{
									flex: isAdminUser ? 0.4 : 'auto',
									width: 'auto',
									textTransform: 'none',
									borderColor: '#5B7EBF',
									color: '#5B7EBF',
									display: 'flex',
									justifyContent: 'center',
									borderRadius: '20px',
									'&:hover': {
										borderColor: '#4B6AA6',
										backgroundColor:
											'rgba(91, 126, 191, 0.04)',
									},
								}}
							>
								View
							</Button>

							{isAdminUser && (
								<Button
									variant='outlined'
									size='small'
									onClick={() =>
										onUnassignClick(studyWithAssignment)
									}
									sx={{
										flex: 'auto',
										textTransform: 'none',
										borderColor: '#5B7EBF',
										color: '#5B7EBF',
										display: 'flex',
										justifyContent: 'center',
										borderRadius: '20px',
										'&:hover': {
											borderColor: '#4B6AA6',
											backgroundColor:
												'rgba(91, 126, 191, 0.04)',
										},
									}}
								>
									Unassign
								</Button>
							)}
						</Box>
					);
				},
			},
		];
	}, [
		isAnnotatorUser,
		isAdminUser,
		onUnassignClick,
		siteSlug,
		userProfile?.id,
	]);

	useEffect(() => {
		if (refreshTable.refreshTable) {
			setQueryState({ ...queryState });
			selectedStudies.current = {};
			setSelectedStudyIds([]);
		}
	}, [refreshTable]);

	useEffect(() => {
		if (!siteSlug) return;
		const params = buildQueryParams(queryState);
		dispatch(
			getStudies({
				request: {
					...params,
					...(isAnnotatorUser && { assigned_to_me: true }),
				},
				siteSlug: siteSlug,
				onSuccess: (response) => {
					setStudiesWithAssignment(response.results);
					setTotalCount(response.info.count);
				},
			}),
		);
	}, [queryState, siteSlug, isAnnotatorUser]);

	return (
		<Box sx={{ position: 'relative' }}>
			<Box
				sx={{
					height: '40px',
					position: 'absolute',
					right: 0,
					top: -1,
				}}
			>
				{isAdminUser &&
					Object.keys(selectedStudies.current).length > 0 && (
						<PrimaryButton
							onClick={handleAssignClick}
							sx={{ zIndex: 20 }}
						>
							Assign Selected (
							{Object.keys(selectedStudies.current).length})
						</PrimaryButton>
					)}
			</Box>

			<DataTable
				columns={columns}
				data={studiesWithAssignment}
				loadingKey={STUDY_LOADING_KEY}
				emptyMessage='No study assignments found'
				paginationInfo={{ totalCount: totalCount }}
				queryState={queryState}
				onQueryChange={setQueryState}
				isTableSelectable={isAdminUser}
				selectedIds={selectedStudyIds}
				onSelectionChange={(ids) =>
					handleSelectionChange(ids.map((id) => Number(id)))
				}
				isRowSelectable={isRowSelectable}
			/>
		</Box>
	);
};
